import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType, IField, ITradesValues } from '../types';
import { SKILLED_NON_SKILLED_OPTIONS, SUPERVISOR_OPTIONS, YES_NO_OPTIONS } from 'utils/optionsUtil';
import { WorkerProfileField } from 'types';
import { shouldShowSkippableIndicator } from 'utils/skippableFieldsUtils';

interface IUseTradesFieldsProps {
  profileFieldConstraintsMap: Record<string, any>;
  languageCode: string;
  values: ITradesValues;
  masterListTrades: Array<{ name: string; id: string }>;
  nonMasterListTrades: Array<{ name: string; id: string }>;
  setValue: (field: keyof ITradesValues, value: any) => void;
  onOtherTradeRemove?: (tradeToRemove: { isSaved: boolean; OtherText: string }) => void;
  hasReachedConsentForm?: boolean;
}

/**
 * Exported field configuration for Trades
 * Used across the app for consistent field mapping
 */
export const TRADES_FIELD_CONFIG: Partial<
  Record<WorkerProfileField, { displayNameKey: string; formField: string; reduxField?: string }>
> = {
  [WorkerProfileField.workerRole]: {
    displayNameKey: 'trades.role',
    formField: 'isSupervisor',
    reduxField: 'isSupervisor',
  },
  [WorkerProfileField.workerLaborUnionStatus]: {
    displayNameKey: 'trades.laborUnion',
    formField: 'isAffiliatedToLaborUnion',
    reduxField: 'isAffiliatedToLaborUnion',
  },
  [WorkerProfileField.workerWorkExperience]: {
    displayNameKey: 'trades.workExperience',
    formField: 'isSkilled',
    reduxField: 'isSkilled',
  },
  [WorkerProfileField.workerLaborUnionNumber]: {
    displayNameKey: 'trades.laborUnionNumber',
    formField: 'laborUnionNumber',
    reduxField: 'laborUnionNumber',
  },
  [WorkerProfileField.workerTrades]: {
    displayNameKey: 'trades.selectedTrades',
    formField: 'trades',
    reduxField: 'trades',
  },
};

/**
 * Custom hook for generating field configurations for trades form
 *
 * @param {IUseTradesFieldsProps} props - Configuration props for field generation
 * @returns {Object} Object containing role fields, trade fields, and other trade fields
 */
export const useTradesFields = ({
  profileFieldConstraintsMap,
  languageCode,
  values,
  masterListTrades,
  nonMasterListTrades,
  setValue,
  onOtherTradeRemove,
  hasReachedConsentForm = false,
}: IUseTradesFieldsProps) => {
  const { t } = useTranslation();

  // Get field display name for modal (translated on-demand)
  const getFieldDisplayName = useCallback(
    (field: WorkerProfileField): string => {
      const config = TRADES_FIELD_CONFIG[field as keyof typeof TRADES_FIELD_CONFIG];
      return config ? t(config.displayNameKey) : field.toString();
    },
    [t],
  );

  // Form field mapping - derived from static config
  const formFieldMap: Partial<Record<WorkerProfileField, string>> = useMemo(() => {
    return Object.entries(TRADES_FIELD_CONFIG).reduce(
      (acc, [field, config]) => {
        acc[field as WorkerProfileField] = config.formField;
        return acc;
      },
      {} as Partial<Record<WorkerProfileField, string>>,
    );
  }, []);

  // Field display names for modal - computed on-demand
  const fieldDisplayNames: Partial<Record<WorkerProfileField, string>> = useMemo(() => {
    return Object.keys(formFieldMap).reduce(
      (acc, fieldKey) => {
        const field = fieldKey as WorkerProfileField;
        acc[field] = getFieldDisplayName(field);
        return acc;
      },
      {} as Partial<Record<WorkerProfileField, string>>,
    );
  }, [getFieldDisplayName, formFieldMap]);

  // Helper function to get dynamic form field map
  const getDynamicFormFieldMap = useCallback(
    (formValues?: ITradesValues): Partial<Record<WorkerProfileField, string>> => {
      return { ...formFieldMap };
    },
    [formFieldMap],
  );

  // Create mapped options for different field types
  const mappedMasterListTrades = useMemo(
    () => masterListTrades?.map(o => ({ label: o.name, value: o.id })) || [],
    [masterListTrades],
  );

  const mappedNonMasterListTrades = useMemo(
    () => nonMasterListTrades?.map(o => ({ label: o.name, value: o.id })) || [],
    [nonMasterListTrades],
  );

  // Create extra chips for other trades
  const extraChips = useMemo(() => {
    return (values?.otherTradesList || [])?.map(trade => {
      if (trade.isSaved) {
        // Find the corresponding option from nonMasterListTrades using the ID
        const foundOption = nonMasterListTrades.find(option => option.id === trade.OtherText);
        return {
          label: foundOption ? foundOption.name : trade.OtherText,
          value: trade.OtherText,
        };
      } else {
        // Use the text as-is for unsaved trades
        return {
          label: trade.OtherText,
          value: trade.OtherText,
        };
      }
    });
  }, [values?.otherTradesList, nonMasterListTrades]);

  // Create onChange handlers for different field types
  const createPickerOnChange = (fieldId: keyof ITradesValues) => (selectedValue: any) => {
    if (fieldId === 'otherTradesList') {
      const newTrade = {
        isSaved: !selectedValue?.isNewAdded,
        OtherText: selectedValue?.value,
      };

      const existingTrades = Array.isArray(values.otherTradesList) ? values.otherTradesList : [];

      const alreadyExists = existingTrades.some(trade => trade.OtherText === newTrade.OtherText);

      if (!alreadyExists && newTrade.OtherText) {
        const updatedTrades = [...existingTrades, newTrade];
        setValue(fieldId, updatedTrades);
      }
    } else {
      setValue(fieldId, selectedValue?.value);
    }
  };

  const createChipsOnChange = (fieldId: keyof ITradesValues) => (selectedChips: any[]) => {
    // Convert array of objects back to array of string IDs for storage
    const selectedIds = selectedChips.map(chip => chip.value);
    setValue(fieldId, selectedIds);
  };

  const handleExtraChipRemove = (chipToRemove: { label: string; value: string }) => {
    // Find the corresponding other trade and remove it
    const tradeToRemove = (values?.otherTradesList || []).find(trade => trade.OtherText === chipToRemove.value);
    if (tradeToRemove && onOtherTradeRemove) {
      onOtherTradeRemove(tradeToRemove);
    }
  };

  // Get selected options for different field types
  const getSelectedOption = (fieldId: keyof ITradesValues, mappedOptions: any[]) => {
    const fieldValue = values[fieldId];
    return mappedOptions.find(option => option.value === fieldValue) || null;
  };

  const getSelectedChips = (fieldId: keyof ITradesValues, mappedOptions: any[]) => {
    const fieldValue = values[fieldId];
    const selectedIds = Array.isArray(fieldValue) ? (fieldValue as string[]) : [];
    return mappedOptions.filter(option => selectedIds.includes(option.value));
  };

  const roleFields: IField[] = useMemo(
    () => [
      {
        name: t('trades.role'),
        id: 'isSupervisor' as keyof ITradesValues,
        type: FieldType.Picker,
        testId: 'isSupervisor-select',
        isRequired: profileFieldConstraintsMap[WorkerProfileField.workerRole]?.isMandatory || false,
        isVisible: profileFieldConstraintsMap[WorkerProfileField.workerRole]?.isVisible || false,
        isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerRole]?.isSkippable,
        skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerRole]?.skippableTill,
        showSkippableIndicator:
          hasReachedConsentForm === false
            ? shouldShowSkippableIndicator(
                WorkerProfileField.workerRole,
                profileFieldConstraintsMap,
                hasReachedConsentForm,
                values.isSupervisor,
              )
            : false,
        options: SUPERVISOR_OPTIONS[languageCode] || [],
        mappedOptions: (SUPERVISOR_OPTIONS[languageCode] || []).map(o => ({ label: o.name, value: o.id })),
        selectedOption: getSelectedOption(
          'isSupervisor',
          (SUPERVISOR_OPTIONS[languageCode] || []).map(o => ({ label: o.name, value: o.id })),
        ),
        onChange: createPickerOnChange('isSupervisor'),
      },
      {
        name: t('trades.laborUnion'),
        id: 'isAffiliatedToLaborUnion' as keyof ITradesValues,
        type: FieldType.Picker,
        testId: 'isAffiliatedToLaborUnion-select',
        isRequired: profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionStatus]?.isMandatory || false,
        isVisible: profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionStatus]?.isVisible || false,
        isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionStatus]?.isSkippable,
        skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionStatus]?.skippableTill,
        showSkippableIndicator:
          hasReachedConsentForm === false
            ? shouldShowSkippableIndicator(
                WorkerProfileField.workerLaborUnionStatus,
                profileFieldConstraintsMap,
                hasReachedConsentForm,
                values.isAffiliatedToLaborUnion,
              )
            : false,
        options: YES_NO_OPTIONS[languageCode] || [],
        mappedOptions: (YES_NO_OPTIONS[languageCode] || []).map(o => ({ label: o.name, value: o.id })),
        selectedOption: getSelectedOption(
          'isAffiliatedToLaborUnion',
          (YES_NO_OPTIONS[languageCode] || []).map(o => ({ label: o.name, value: o.id })),
        ),
        onChange: createPickerOnChange('isAffiliatedToLaborUnion'),
      },
      {
        name: t('trades.workExperience'),
        id: 'isSkilled' as keyof ITradesValues,
        type: FieldType.Picker,
        testId: 'isSkilled-select',
        isRequired: profileFieldConstraintsMap[WorkerProfileField.workerWorkExperience]?.isMandatory || false,
        isVisible: profileFieldConstraintsMap[WorkerProfileField.workerWorkExperience]?.isVisible || false,
        isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerWorkExperience]?.isSkippable,
        skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerWorkExperience]?.skippableTill,
        showSkippableIndicator:
          hasReachedConsentForm === false
            ? shouldShowSkippableIndicator(
                WorkerProfileField.workerWorkExperience,
                profileFieldConstraintsMap,
                hasReachedConsentForm,
                values.isSkilled,
              )
            : false,
        options: SKILLED_NON_SKILLED_OPTIONS[languageCode] || [],
        mappedOptions: (SKILLED_NON_SKILLED_OPTIONS[languageCode] || []).map(o => ({ label: o.name, value: o.id })),
        selectedOption: getSelectedOption(
          'isSkilled',
          (SKILLED_NON_SKILLED_OPTIONS[languageCode] || []).map(o => ({ label: o.name, value: o.id })),
        ),
        onChange: createPickerOnChange('isSkilled'),
      },
      {
        name: t('trades.laborUnionNumber'),
        id: 'laborUnionNumber' as keyof ITradesValues,
        type: FieldType.Text,
        testId: 'laborUnionNumber-input',
        // Labor union number becomes mandatory once the worker selects labor union = YES
        // (see the schema's `.when('isAffiliatedToLaborUnion')` rule). The field is only ever
        // visible — and thus only reaches the sidebar — when YES is selected, so treat it as
        // required in that case so it shows in the sidebar step list.
        isRequired: values?.isAffiliatedToLaborUnion === true,
        isVisible:
          (profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionNumber]?.isVisible || false) &&
          values?.isAffiliatedToLaborUnion === true,
        isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionNumber]?.isSkippable,
        skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerLaborUnionNumber]?.skippableTill,
        showSkippableIndicator:
          hasReachedConsentForm === false
            ? shouldShowSkippableIndicator(
                WorkerProfileField.workerLaborUnionNumber,
                profileFieldConstraintsMap,
                hasReachedConsentForm,
                values.laborUnionNumber,
              )
            : false,
        onChange: (value: string) => setValue('laborUnionNumber', value),
      },
    ],
    [t, profileFieldConstraintsMap, languageCode, values, setValue, hasReachedConsentForm],
  );

  const tradeFields: IField[] = useMemo(
    () => [
      {
        name: t('trades.selectedTrades'),
        id: 'trades' as keyof ITradesValues,
        type: FieldType.Chips,
        testId: 'trades-chips',
        isRequired: profileFieldConstraintsMap[WorkerProfileField.workerTrades]?.isMandatory || false,
        isVisible: profileFieldConstraintsMap[WorkerProfileField.workerTrades]?.isVisible || false,
        isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerTrades]?.isSkippable,
        skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerTrades]?.skippableTill,
        showSkippableIndicator:
          hasReachedConsentForm === false
            ? shouldShowSkippableIndicator(
                WorkerProfileField.workerTrades,
                profileFieldConstraintsMap,
                hasReachedConsentForm,
                values.trades,
              )
            : false,
        options: masterListTrades,
        mappedOptions: mappedMasterListTrades,
        selectedChips: getSelectedChips('trades', mappedMasterListTrades),
        extraChips: extraChips,
        onChange: createChipsOnChange('trades'),
        onExtraChipRemove: handleExtraChipRemove,
        otherTrades: values?.otherTradesList || [],
        savedOtherTrades: nonMasterListTrades || [],
      },
    ],
    [
      t,
      profileFieldConstraintsMap,
      masterListTrades,
      mappedMasterListTrades,
      extraChips,
      values,
      nonMasterListTrades,
      setValue,
      onOtherTradeRemove,
      hasReachedConsentForm,
    ],
  );

  const otherTradeFields: IField[] = useMemo(
    () => [
      {
        name: t('trades.otherTrade'),
        id: 'otherTradesList' as keyof ITradesValues,
        type: FieldType.Picker,
        testId: 'otherTradesList-select',
        isRequired: false,
        isVisible: profileFieldConstraintsMap[WorkerProfileField.workerTrades]?.isVisible || false,
        options: nonMasterListTrades,
        mappedOptions: mappedNonMasterListTrades,
        selectedOption: null, // Always null for adding new trades
        onChange: createPickerOnChange('otherTradesList'),
        addOption: true,
      },
    ],
    [t, profileFieldConstraintsMap, nonMasterListTrades, mappedNonMasterListTrades, setValue],
  );

  const isRoleFieldsSectionVisible = useMemo(() => roleFields.some(field => field.isVisible), [roleFields]);
  const isTradeFieldsSectionVisible = useMemo(() => tradeFields.some(field => field.isVisible), [tradeFields]);

  return {
    roleFields,
    tradeFields,
    otherTradeFields,
    isRoleFieldsSectionVisible,
    isTradeFieldsSectionVisible,
    fieldDisplayNames,
    getDynamicFormFieldMap,
    formFieldMap,
  };
};
